import { Snakelize } from "@shared/entities";
import { serializeCamelToSnake, serializeSnakeToCamel } from "@shared/utils";
import { useMutation, UseMutationResult, useQuery, UseQueryResult } from "@tanstack/react-query";
import { api } from "app/api/client";
import {
  ErrorResponse,
  IResource,
  TQueryKey,
  TQueryOptions,
  TPostMutationOptions,
  TPatchMutationOptions,
  TDeleteMutationOptions
} from "app/queryProvider/interfaces";
import { generateUrl } from "./generateRequestUrl";

export const useFetch = <TResponse>({
    queryKey,
    url,
    resource,
    headers,
    options,
    serializer,
    requireResourceId,
  }: {
    queryKey: TQueryKey;
    url: string;
    resource: IResource;
    headers?: Record<string, string>;
    options?: TQueryOptions<TResponse>;
    serializer?: {
      response: (data: TResponse) => TResponse;
    };
    /**
     * When true, the request is never sent if `resource.id` is missing, not even via a manual `refetch()`
     * (which ignores `enabled`). The query fails with an error instead of calling an URL without the id.
     */
    requireResourceId?: boolean;
  }): Omit<UseQueryResult<TResponse, ErrorResponse>, "data"> & {
    data: TResponse | undefined;
  } => {
    const generatedUrl = generateUrl(resource, url)
    const isResourceIdMissing = !!requireResourceId && !resource.id

    const { data, ...rest } = useQuery<
      TResponse,
      ErrorResponse,
      TResponse,
      TQueryKey
    >({
      queryKey,
      queryFn: () => {
        if (isResourceIdMissing) {
          return Promise.reject(new Error(`Missing id for resource "${resource.name}"`));
        }
        return api.get<TResponse>(generatedUrl, { headers });
      },
      ...options,
      ...(isResourceIdMissing && { retry: false }),
    });

    const transformedData =
      data && serializer?.response ? serializer.response(data) : serializeSnakeToCamel<TResponse>(data);

    return {
      ...rest,
      data: transformedData,
    };
  };

  export const usePost = <TVariables, TResponse>({
    headers,
    serializer,
    options,
  }: {
    headers?: Record<string, string>;
    serializer?: {
      request: (data: TVariables) => Snakelize<TVariables>;
      response: (data: TResponse) => TResponse;
    };
    options?: TPostMutationOptions<TResponse, TVariables>;
  } = {}): UseMutationResult<
    TResponse,
    ErrorResponse,
    { resource: IResource; url: string; data: TVariables }
  > => {
    return useMutation<
      TResponse,
      ErrorResponse,
      { resource: IResource; url: string; data: TVariables }
    >({
      mutationFn: async ({ resource, url, data }) => {
        const generatedUrl = generateUrl(resource, url)
        const serializedData = serializer?.request ? serializer.request(data) : serializeCamelToSnake<TVariables>(data);
        const response = await api.post<TResponse, Snakelize<TVariables>>(
          generatedUrl,
          serializedData,
          { headers }
        );

        return serializer?.response ? serializer.response(response) : serializeSnakeToCamel<TResponse>(response);
      },
      ...options,
    });
  };

  export const usePatch = <TVariables, TResponse>({
    headers,
    serializer,
    options,
  }: {
    headers?: Record<string, string>;
    serializer?: {
      request: (data: TVariables) => Snakelize<TVariables>;
      response: (data: TResponse) => TResponse;
    };
    options?: TPatchMutationOptions<TResponse, TVariables>;
  } = {}): UseMutationResult<
    TResponse,
    ErrorResponse,
    { resource: IResource; url: string; data: TVariables }
  > => {
    return useMutation<
      TResponse,
      ErrorResponse,
      { resource: IResource; url: string; data: TVariables }
    >({
      mutationFn: async ({ resource, url, data }) => {
        const generatedUrl = generateUrl(resource, url)
        const serializedData = serializer?.request ? serializer.request(data) : serializeCamelToSnake<TVariables>(data);
        const response = await api.patch<TResponse, Snakelize<TVariables>>(
          generatedUrl,
          serializedData,
          { headers }
        );

        return serializer?.response ? serializer.response(response) : serializeSnakeToCamel<TResponse>(response);
      },
      ...options,
    });
  };

  export const usePut = <TVariables, TResponse>({
    headers,
    serializer,
    options,
  }: {
    headers?: Record<string, string>;
    serializer?: {
      request: (data: TVariables) => Snakelize<TVariables>;
      response: (data: TResponse) => TResponse;
    };
    options?: TPatchMutationOptions<TResponse, TVariables>;
  } = {}): UseMutationResult<
    TResponse,
    ErrorResponse,
    { resource: IResource; url: string; data: TVariables }
  > => {
    return useMutation<
      TResponse,
      ErrorResponse,
      { resource: IResource; url: string; data: TVariables }
    >({
      mutationFn: async ({ resource, url, data }) => {
        const generatedUrl = generateUrl(resource, url)
        const serializedData = serializer?.request ? serializer.request(data) : serializeCamelToSnake<TVariables>(data);
        const response = await api.put<TResponse, Snakelize<TVariables>>(
          generatedUrl,
          serializedData,
          { headers }
        );

        return serializer?.response ? serializer.response(response) : serializeSnakeToCamel<TResponse>(response);
      },
      ...options,
    });
  };

  export const useDelete = <TVariables, TResponse>({
    headers,
    options,
  }: {
    headers?: Record<string, string>;
    options?: TDeleteMutationOptions<TResponse, TVariables>;
  } = {}): UseMutationResult<
    TResponse,
    ErrorResponse,
    { resource: IResource; url: string; data?: TVariables }
  > => {
    return useMutation<
      TResponse,
      ErrorResponse,
      { resource: IResource;  url: string; data?: TVariables }
    >({
      mutationFn: async ({ resource, url }) => {
        const generatedUrl = generateUrl(resource, url)
        const response = await api.del<TResponse>(generatedUrl, { headers });
        return response;
      },
      ...options,
    });
  };